import React from "react";
import { View, ScrollView, TouchableOpacity, Platform, Linking } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter } from "expo-router";
import { useQuery } from "@tanstack/react-query";
import { AppText } from "@/components/ui/AppText";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { SuccessState } from "@/components/ui/SuccessState";
import { ErrorState } from "@/components/ui/ErrorState";
import { useI18n } from "@/context/I18nContext";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Icon } from "@/components/ui/Icon";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { PageFooter } from "@/components/ui/PageFooter";
import { StepIndicator, type Step as ReviewStep } from "@/components/ui/StepIndicator";
import {
  vendorApi,
  type VendorDashboardData,
  type RejectedDocumentData,
  type PendingUpdateField,
} from "@/lib/api/vendor";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { formatAbsoluteDate } from "@/lib/dateFormat";

// ─── Query key ────────────────────────────────────────────────────────────

export const VENDOR_DASHBOARD_QUERY_KEY = ["vendor", "dashboard"] as const;

// ─── Types ───────────────────────────────────────────────────────────────────

/** Application status variants that drive the dashboard layout. */
export type VendorApplicationStatus = "pending" | "approved" | "rejected" | "update_required";

// ReviewStep and ReviewStepStatus are re-exported from StepIndicator.
// ReviewStepStatus: "completed" | "current" | "pending" | "failed"
// ReviewStep: { label: TranslationKeys; description?: TranslationKeys; status: ReviewStepStatus }

interface NextStep {
  number: string;
  title: TranslationKeys;
  description: TranslationKeys;
}

// ─── Data ────────────────────────────────────────────────────────────────────

const PENDING_REVIEW_STEPS: readonly ReviewStep[] = [
  { label: "vendor.dashboard.step.submitted", status: "completed" },
  { label: "vendor.dashboard.step.review", status: "current" },
  { label: "vendor.dashboard.step.approve", status: "pending" },
] as const;

const APPROVED_REVIEW_STEPS: readonly ReviewStep[] = [
  { label: "vendor.dashboard.step.submitted", status: "completed" },
  { label: "vendor.dashboard.step.review", status: "completed" },
  { label: "vendor.dashboard.step.approve", status: "completed" },
] as const;

const REJECTED_REVIEW_STEPS: readonly ReviewStep[] = [
  { label: "vendor.dashboard.step.submitted", status: "completed" },
  { label: "vendor.dashboard.step.review", status: "completed" },
  { label: "vendor.dashboard.step.approve", status: "failed" },
] as const;

// "Update Required" uses the same visual steps as "Pending" — back at the Review node.
const UPDATE_REQUIRED_REVIEW_STEPS: readonly ReviewStep[] = [
  { label: "vendor.dashboard.step.submitted", status: "completed" },
  { label: "vendor.dashboard.step.review", status: "current" },
  { label: "vendor.dashboard.step.approve", status: "pending" },
] as const;

const PENDING_NEXT_STEPS: readonly NextStep[] = [
  {
    number: "01",
    title: "vendor.dashboard.nextSteps.review.title",
    description: "vendor.dashboard.nextSteps.review.description",
  },
  {
    number: "02",
    title: "vendor.dashboard.nextSteps.validation.title",
    description: "vendor.dashboard.nextSteps.validation.description",
  },
  {
    number: "03",
    title: "vendor.dashboard.nextSteps.decision.title",
    description: "vendor.dashboard.nextSteps.decision.description",
  },
] as const;

const APPROVED_NEXT_STEPS: readonly NextStep[] = [
  {
    number: "01",
    title: "vendor.dashboard.nextSteps.approved.01.title",
    description: "vendor.dashboard.nextSteps.approved.01.description",
  },
  {
    number: "02",
    title: "vendor.dashboard.nextSteps.approved.02.title",
    description: "vendor.dashboard.nextSteps.approved.02.description",
  },
  {
    number: "03",
    title: "vendor.dashboard.nextSteps.approved.03.title",
    description: "vendor.dashboard.nextSteps.approved.03.description",
  },
] as const;

// ─── Hero band container ────────────────────────────────────────────────────

function HeroBandContainer({ children, isWide }: { children: React.ReactNode; isWide: boolean }) {
  if (!isWide || Platform.OS !== "web") {
    return (
      <LinearGradient
        colors={["#061633", "#061633", "#113A88"]}
        locations={[0, 0.152, 1.0]}
        start={{ x: 0.34, y: 0.97 }}
        end={{ x: 0.66, y: 0.03 }}
      >
        {children}
      </LinearGradient>
    );
  }
  return <View style={{ backgroundColor: "#09204D" }}>{children}</View>;
}

// ─── Main screen ─────────────────────────────────────────────────────────────

export default function VendorHomeScreen() {
  const { t, isRTL, locale } = useI18n();
  const { top } = useSafeAreaInsets();
  const isWide = useIsWideScreen();
  const router = useRouter();
  const { user } = useAuth();
  const applicationId = user?.applicationId ?? null;

  const { data } = useQuery<VendorDashboardData>({
    queryKey: [...VENDOR_DASHBOARD_QUERY_KEY, applicationId],
    queryFn: () => vendorApi.getDashboardInfo(applicationId!),
    enabled: !!applicationId,
  });

  const applicationStatus = data?.applicationStatus ?? "pending";
  const statusDate = data?.statusDate;
  const rejectionReason = data?.rejectionReason;
  const rejectedDocuments: RejectedDocumentData[] = data?.rejectedDocuments ?? [];
  const pendingUpdates: PendingUpdateField[] = data?.pendingUpdates ?? [];

  const isApproved = applicationStatus === "approved";
  const isRejected = applicationStatus === "rejected";
  const isUpdateRequired = applicationStatus === "update_required";
  const reviewSteps = isApproved
    ? APPROVED_REVIEW_STEPS
    : isRejected
      ? REJECTED_REVIEW_STEPS
      : isUpdateRequired
        ? UPDATE_REQUIRED_REVIEW_STEPS
        : PENDING_REVIEW_STEPS;
  const nextSteps = isApproved ? APPROVED_NEXT_STEPS : PENDING_NEXT_STEPS;

  // Date shown in the stepper card top row
  const rawDate = statusDate ?? (isApproved || isRejected ? "Mar 04, 2026" : "Mar 02, 2026");
  const displayDate = formatAbsoluteDate(rawDate, locale);
  const dateLabel = isApproved
    ? t("vendor.dashboard.approvedOn", { date: displayDate })
    : isRejected
      ? t("vendor.dashboard.rejectedOn", { date: displayDate })
      : t("vendor.dashboard.submittedOn", { date: displayDate });

  const handleGoToMerchantPortal = () => {
    const portalUrl = process.env.EXPO_PUBLIC_MERCHANT_PORTAL_URL;
    if (portalUrl) {
      Linking.openURL(portalUrl);
    }
  };

  const handleContactSupport = () => {
    // TODO: navigate to support screen once route is created
    router.push("/");
  };

  const handleGoToField = (field: PendingUpdateField) => {
    router.push({
      pathname: "/(vendor)/application/[stage]/update",
      params: { stage: field.stageId, fieldKey: field.fieldKey },
    });
  };

  return (
    <ScrollView
      className={`flex-1 ${isWide ? "bg-stroke-20" : "bg-white"}`}
      contentContainerStyle={{ flexGrow: 1 }}
      testID="vendor-dashboard"
    >
      {/* ── Large screen: white page heading (outside dark status band) ── */}
      {isWide && Platform.OS === "web" && (
        <View className="bg-white px-20 pt-6 pb-6 items-start">
          <AppText
            weight="bold"
            className="text-[29px] leading-[32px] tracking-[-0.29px] text-dark-text-50"
          >
            {t("vendor.dashboard.title")}
          </AppText>
          <AppText weight="medium" className="text-base text-supporting-text-40 mt-1">
            {t("vendor.dashboard.subtitle")}
          </AppText>
        </View>
      )}

      {/* ── Dark navy application status band ───────────────────────── */}
      <HeroBandContainer isWide={isWide}>
        {/* Mobile only: "Home" title row + bell notification button */}
        {(!isWide || Platform.OS !== "web") && (
          <View
            className={`flex-row items-center justify-between pb-3 ${isWide ? "px-20" : "px-page-x"}`}
            style={{ paddingTop: top + 16 }}
          >
            <AppText
              weight="semibold"
              className="text-[29px] leading-[32px] tracking-[-0.29px] text-white"
            >
              {t("vendor.dashboard.title")}
            </AppText>
            <TouchableOpacity
              className="size-10 rounded-full items-center justify-center"
              style={{ backgroundColor: "#09204D" }}
              accessibilityRole="button"
              accessibilityLabel="Notifications"
            >
              <Icon
                as={MaterialCommunityIcons}
                name="bell-outline"
                size={22}
                className="text-white"
              />
            </TouchableOpacity>
          </View>
        )}

        {/* Status section: "YOUR APPLICATION STATUS" label + stepper card */}
        <View className={`items-start w-full gap-4 ${isWide ? "px-20 py-6" : "px-page-x pb-6"}`}>
          <AppText
            weight="semibold"
            className="text-sm tracking-[0.14px] text-stroke-10 uppercase flex-1"
          >
            {t("vendor.dashboard.applicationStatus")}
          </AppText>

          {/* White stepper card */}
          <View
            className={`w-full bg-white border border-stroke-50 rounded-2xl gap-4 overflow-hidden ${
              isWide ? "px-6 py-5" : "p-4"
            }`}
          >
            {/* Top row: badge + date / count */}
            <View className="flex-row items-center justify-between">
              <Badge
                variant={
                  isApproved ? "filled-success" : isRejected ? "filled-error" : "filled-warning"
                }
                shape="rounded"
                label={
                  isApproved
                    ? "vendor.dashboard.status.approved"
                    : isRejected
                      ? "vendor.dashboard.status.notApproved"
                      : isUpdateRequired
                        ? "vendor.dashboard.status.updateRequired"
                        : "vendor.dashboard.status.pending"
                }
                testID="stepper-card-badge"
              />
              {isUpdateRequired ? (
                <AppText
                  weight="medium"
                  className="text-sm text-supporting-text-40"
                  testID="stepper-card-count"
                >
                  {t("vendor.dashboard.pendingUpdates.fieldsToUpdate", {
                    count: pendingUpdates.length,
                  })}
                </AppText>
              ) : (
                <AppText
                  weight="medium"
                  className="text-sm text-supporting-text-40"
                  testID="stepper-card-date"
                >
                  {dateLabel}
                </AppText>
              )}
            </View>
            <StepIndicator
              orientation="horizontal"
              variant="dashboard"
              steps={reviewSteps}
              testID="application-stepper"
            />
          </View>
        </View>
      </HeroBandContainer>

      {/* ── Content area ────────────────────────────────────────────── */}
      <View className={`gap-6 ${isWide ? "px-20 py-6" : "pb-4"}`}>
        {/* ── Approved state: Congratulations card ───────────────────── */}
        {isApproved ? (
          <View
            testID="approved-state-card"
            className={`bg-white border border-stroke-50 overflow-hidden ${
              isWide ? "rounded-lg" : "mx-page-x mt-6 rounded-2xl"
            }`}
          >
            <SuccessState
              title="vendor.dashboard.approvedState.title"
              description="vendor.dashboard.approvedState.description"
              onPrimaryAction={handleGoToMerchantPortal}
              primaryActionLabel="vendor.dashboard.approvedState.cta"
              buttonVariant="primary"
              className="px-8 py-10 gap-6"
              icon={
                <View className="size-40 rounded-full bg-green-5 items-center justify-center">
                  <Icon
                    as={MaterialCommunityIcons}
                    name="check"
                    size={64}
                    className="text-green-50"
                  />
                </View>
              }
            />
          </View>
        ) : isRejected ? (
          /* ── Rejected state: reason for rejection card ────────────── */
          <View
            testID="rejected-state-card"
            className={`bg-white border border-stroke-50 overflow-hidden ${
              isWide ? "rounded-lg" : "mx-page-x mt-6 rounded-2xl"
            }`}
          >
            <ErrorState
              title="vendor.dashboard.rejectedState.title"
              description="vendor.dashboard.rejectedState.defaultDescription"
              descriptionText={rejectionReason}
              onSecondaryAction={handleContactSupport}
              secondaryActionLabel="vendor.dashboard.rejectedState.contactSupport"
              className="px-8 py-10 gap-6"
              icon={
                <View className="size-40 rounded-full bg-[#FCE6E7] items-center justify-center">
                  <Icon as={MaterialCommunityIcons} name="close" size={64} className="text-error" />
                </View>
              }
            />
          </View>
        ) : !isUpdateRequired ? (
          /* ── Pending state: empty state card ──────────────────────── */
          <View
            testID="empty-state-card"
            className={`bg-white border border-stroke-50 overflow-hidden ${
              isWide ? "rounded-lg" : "mx-page-x mt-6 rounded-2xl"
            }`}
          >
            <EmptyState
              title="vendor.dashboard.emptyState.title"
              description="vendor.dashboard.emptyState.description"
              className="px-8 py-10 gap-6"
              icon={
                <View className="size-40 rounded-full bg-primary-5 items-center justify-center">
                  <Icon
                    as={MaterialCommunityIcons}
                    name="file-search-outline"
                    size={64}
                    className="text-primary"
                  />
                </View>
              }
            />
          </View>
        ) : null}

        {/* ── Rejected state: Rejected Documents section ──────────── */}
        {isRejected ? (
          <View
            testID="rejected-documents-card"
            className={`bg-white overflow-hidden ${
              isWide ? "rounded-lg border border-stroke-50" : "border-t border-stroke-50"
            }`}
          >
            {/* Section header */}
            <View className={isWide ? "bg-stroke-40 px-10 py-4" : "bg-stroke-30 px-page-x py-3"}>
              <AppText
                weight={isWide ? "semibold" : "bold"}
                className={`uppercase text-dark-text-50 ${
                  isWide ? "text-base" : "text-sm tracking-[0.14px]"
                }`}
              >
                {t("vendor.dashboard.rejectedDocuments.title")}
              </AppText>
            </View>
            <View className="border-t border-stroke-50" />
            {/* Document items */}
            <View>
              {rejectedDocuments.map((doc, i) => (
                <React.Fragment key={i}>
                  <View className={isWide ? "px-12 py-6" : "px-page-x py-6"}>
                    {/* Document name label */}
                    <AppText weight="semibold" className="text-base text-dark-text-50">
                      {doc.name ? t(doc.name as TranslationKeys) : ""}
                    </AppText>
                    {/* File info or identifier value */}
                    {doc.file != null ? (
                      <View className="flex-row gap-4 py-4 items-start">
                        <View className="size-10 rounded-full bg-blue-5 items-center justify-center shrink-0 border-blue-5">
                          <Icon
                            as={MaterialCommunityIcons}
                            name="file-outline"
                            size={20}
                            className="text-blue-50"
                          />
                        </View>
                        <View className="flex-1">
                          <AppText weight="medium" className="text-base text-dark-text-50">
                            {doc.file.name}
                          </AppText>
                          <AppText weight="medium" className="text-base text-supporting-text-40">
                            {doc.file.size}
                          </AppText>
                          {doc.file.url != null && (
                            <TouchableOpacity
                              onPress={() => {
                                /* TODO: open doc.file.url */
                              }}
                            >
                              <AppText weight="medium" className="text-base text-blue-50">
                                {t("vendor.dashboard.rejectedDocuments.viewDocument")}
                              </AppText>
                            </TouchableOpacity>
                          )}
                        </View>
                      </View>
                    ) : doc.identifier != null ? (
                      <AppText weight="medium" className="text-base text-dark-text-50 py-4">
                        {doc.identifier}
                      </AppText>
                    ) : null}
                    {/* Actions row: Rejected badge + View Reason button */}
                    <View className="flex-row gap-3 items-center py-2">
                      <Badge
                        variant="filled-error"
                        shape="rounded"
                        label="vendor.dashboard.rejectedDocuments.rejected"
                        className="px-4 lg:px-6 py-2 lg:py-3"
                        labelClassName="text-base"
                      />
                      <Button
                        variant="secondary"
                        labelClassName="text-supporting-text"
                        label="vendor.dashboard.rejectedDocuments.viewReason"
                        onPress={() => {
                          /* TODO: show rejection reason modal */
                        }}
                      />
                    </View>
                  </View>
                  {i < rejectedDocuments.length - 1 && (
                    <View className="border-b border-stroke-50" />
                  )}
                </React.Fragment>
              ))}
            </View>
          </View>
        ) : isUpdateRequired ? (
          /* ── Update Required: Pending Updates section ──────────────── */
          <View
            testID="pending-updates-card"
            className={`bg-white overflow-hidden ${
              isWide ? "rounded-lg border border-stroke-50" : "border-t border-stroke-50"
            }`}
          >
            {/* Section header */}
            <View
              className={`flex-row items-center gap-2 ${
                isWide ? "bg-stroke-40 px-10 py-4" : "bg-stroke-30 px-page-x py-3"
              }`}
            >
              <AppText
                weight={isWide ? "semibold" : "bold"}
                className={`uppercase text-dark-text-50 ${
                  isWide ? "text-base" : "text-sm tracking-[0.14px]"
                }`}
              >
                {t("vendor.dashboard.pendingUpdates.title")}
              </AppText>
              <View className="bg-white rounded-full size-[22px] items-center justify-center">
                <AppText
                  weight="semibold"
                  className="text-[13px] leading-[1.55] tracking-[0.52px] text-dark-text-50 text-center"
                >
                  {pendingUpdates.length}
                </AppText>
              </View>
            </View>
            <View className="border-t border-stroke-50" />
            {/* Field rows */}
            <View className={isWide ? "p-4 gap-4" : "p-4 gap-4"}>
              {pendingUpdates.length === 0 ? (
                <AppText className="text-sm text-supporting-text-40 text-center py-2">
                  {t("vendor.dashboard.pendingUpdates.noFieldsSpecified")}
                </AppText>
              ) : (
                pendingUpdates.map((field, i) => (
                  <TouchableOpacity
                    key={i}
                    testID={`pending-update-row-${i}`}
                    className="bg-white border border-stroke-50 rounded-lg overflow-hidden"
                    onPress={() => handleGoToField(field)}
                    accessibilityRole="button"
                  >
                    <View className="flex-row items-center gap-2 p-4">
                      <View className="flex-1 gap-1">
                        <AppText weight="semibold" className="text-base text-dark-text-50">
                          {field.name ? t(field.name as TranslationKeys) : ""}
                        </AppText>
                        <AppText weight="medium" className="text-sm text-supporting-text-40">
                          {field.category ? t(field.category as TranslationKeys) : ""}
                        </AppText>
                      </View>
                      <Icon
                        as={MaterialCommunityIcons}
                        name={isRTL ? "chevron-left" : "chevron-right"}
                        size={24}
                        className="text-supporting-text"
                      />
                    </View>
                  </TouchableOpacity>
                ))
              )}
            </View>
          </View>
        ) : (
          /* ── Next Steps (pending / approved) ───────────────────────── */
          <View
            testID="next-steps-card"
            className={`bg-white overflow-hidden ${
              isWide ? "rounded-lg border border-stroke-50" : "border-t border-stroke-50"
            }`}
          >
            {/* Section header */}
            <View
              className={cn(
                "items-start",
                isWide ? "bg-stroke-40 px-10 py-4" : "bg-stroke-30 px-4 py-3"
              )}
            >
              <AppText
                weight={isWide ? "semibold" : "bold"}
                className={`uppercase text-dark-text-50 ${
                  isWide ? "text-base" : "text-sm tracking-[0.14px]"
                }`}
              >
                {t("vendor.dashboard.nextSteps.title")}
              </AppText>
            </View>
            <View className="border-t border-stroke-50" />
            {/* Items */}
            <View className={isWide ? "" : "px-2"}>
              {nextSteps.map((step, i) => (
                <React.Fragment key={step.number}>
                  <View className={`flex-row gap-3 items-start ${isWide ? "px-12 py-6" : "p-6"}`}>
                    <AppText weight="semibold" className="text-base text-supporting-text-40">
                      {step.number}
                    </AppText>
                    <View className="flex-1 items-start gap-2">
                      <AppText weight="semibold" className="text-base text-dark-text-50">
                        {t(step.title)}
                      </AppText>
                      <AppText weight="medium" className="text-base text-supporting-text-40">
                        {t(step.description)}
                      </AppText>
                    </View>
                  </View>
                  {i < nextSteps.length - 1 && <View className="border-b border-stroke-50" />}
                </React.Fragment>
              ))}
            </View>
          </View>
        )}
      </View>
      <PageFooter />
    </ScrollView>
  );
}
